import React, { useState } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Plus, Trash2, GripVertical, Eye, EyeOff, ChevronDown, ChevronUp, Handshake, ExternalLink, Upload, Loader2, X } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog';
import toast from 'react-hot-toast';
import { compressImage, formatBytes } from '@/lib/image-utils';
import type { Partner, BilingualText } from '@/types';

const INPUT_CLS = "w-full px-4 py-3 bg-black/[0.03] border border-black/[0.06] rounded-xl text-sm font-medium outline-none focus:border-black/20 transition-all placeholder:text-black/20";
const LABEL_CLS = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 mb-1.5";

const AdminPartnersPage: React.FC = () => {
  const partners = useAdminStore((s) => s.partners);
  const addPartner = useAdminStore((s) => s.addPartner);
  const updatePartner = useAdminStore((s) => s.updatePartner);
  const deletePartner = useAdminStore((s) => s.deletePartner);
  const reorderPartners = useAdminStore((s) => s.reorderPartners);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState<string | null>(null);

  const sorted = [...(partners ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);
  const orderedIds = sorted.map((p) => p._id);

  const handleAdd = (): void => {
    const newPartner = addPartner({
      name: { en: '', ka: '' },
      description: { en: '', ka: '' },
      category: { en: '', ka: '' },
      logo: '',
      url: '',
      displayOrder: sorted.length,
      isActive: true,
    });
    setExpandedId(newPartner._id);
    toast.success('New partner added');
  };

  const handleDelete = (): void => {
    if (!deleteId) return;
    deletePartner(deleteId);
    toast.success('Partner deleted');
    setDeleteId(null);
    if (expandedId === deleteId) setExpandedId(null);
  };

  const handleReorder = (newOrder: string[]): void => {
    reorderPartners(newOrder);
  };

  const updateBil = (id: string, field: 'name' | 'description' | 'category', lang: 'en' | 'ka', value: string): void => {
    const partner = sorted.find((p) => p._id === id);
    if (!partner) return;
    updatePartner(id, {
      [field]: { ...partner[field], [lang]: value },
    });
  };

  const handleLogoUpload = (id: string, file: File): void => {
    setLogoLoading(id);
    compressImage(file, { maxWidth: 400, maxHeight: 400, quality: 0.85 })
      .then(({ dataUrl }) => {
        updatePartner(id, { logo: dataUrl });
        toast.success('Logo uploaded');
      })
      .catch(() => toast.error('Failed to upload logo'))
      .finally(() => setLogoLoading(null));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Partners</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20">
            {sorted.length} partner{sorted.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-5 py-3 bg-black text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity"
        >
          <Plus size={14} /> Add Partner
        </button>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-20">
          <Handshake size={32} className="mx-auto text-black/10 mb-4" />
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20">No partners yet</p>
          <p className="text-[10px] text-black/15 mt-1">Add your first partner to get started</p>
        </div>
      ) : (
        <Reorder.Group axis="y" values={orderedIds} onReorder={handleReorder} className="space-y-3">
          <AnimatePresence mode="popLayout">
            {sorted.map((partner) => {
              const isExpanded = expandedId === partner._id;
              return (
                <Reorder.Item
                  key={partner._id}
                  value={partner._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-white border border-black/[0.04] rounded-2xl overflow-hidden hover:border-black/10 transition-all"
                >
                  {/* Header */}
                  <div className="flex items-center gap-3 px-4 py-3.5">
                    <GripVertical size={14} className="text-black/15 cursor-grab active:cursor-grabbing shrink-0" />
                    {partner.logo && (
                      <img src={partner.logo} alt="" className="w-8 h-8 rounded-lg object-contain bg-black/[0.03] shrink-0" />
                    )}
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : partner._id)}
                      className="flex-1 text-left min-w-0"
                    >
                      <p className="text-[13px] font-bold truncate">
                        {partner.name.en || partner.name.ka || 'Untitled partner'}
                      </p>
                      {partner.category.en && (
                        <p className="text-[10px] text-black/30 truncate mt-0.5">{partner.category.en}</p>
                      )}
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => updatePartner(partner._id, { isActive: !partner.isActive })}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                          partner.isActive ? 'text-green-500 hover:bg-green-500/5' : 'text-black/20 hover:bg-black/[0.04]'
                        }`}
                      >
                        {partner.isActive ? <Eye size={13} /> : <EyeOff size={13} />}
                      </button>
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : partner._id)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 hover:text-black hover:bg-black/[0.04] transition-all"
                      >
                        {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </button>
                      <button
                        onClick={() => setDeleteId(partner._id)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 hover:text-red-500 hover:bg-red-500/5 transition-all"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Expanded form */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      transition={{ duration: 0.25 }}
                      className="border-t border-black/[0.04]"
                    >
                      <div className="p-5 space-y-4">
                        {/* Name */}
                        <div className="space-y-2">
                          <label className={LABEL_CLS}>Partner Name</label>
                          <div className="relative">
                            <span className="absolute left-3.5 top-3 text-[8px] font-bold uppercase tracking-[0.15em] text-black/25">EN</span>
                            <input value={partner.name.en} onChange={(e) => updateBil(partner._id, 'name', 'en', e.target.value)} placeholder="Partner name..." className={INPUT_CLS + " pl-10"} />
                          </div>
                          <div className="relative">
                            <span className="absolute left-3.5 top-3 text-[8px] font-bold uppercase tracking-[0.15em] text-black/25">KA</span>
                            <input value={partner.name.ka} onChange={(e) => updateBil(partner._id, 'name', 'ka', e.target.value)} placeholder="პარტნიორის სახელი..." className={INPUT_CLS + " pl-10"} />
                          </div>
                        </div>

                        {/* Category */}
                        <div className="space-y-2">
                          <label className={LABEL_CLS}>Category</label>
                          <div className="relative">
                            <span className="absolute left-3.5 top-3 text-[8px] font-bold uppercase tracking-[0.15em] text-black/25">EN</span>
                            <input value={partner.category.en} onChange={(e) => updateBil(partner._id, 'category', 'en', e.target.value)} placeholder="e.g. Museum, Art Fair..." className={INPUT_CLS + " pl-10"} />
                          </div>
                          <div className="relative">
                            <span className="absolute left-3.5 top-3 text-[8px] font-bold uppercase tracking-[0.15em] text-black/25">KA</span>
                            <input value={partner.category.ka} onChange={(e) => updateBil(partner._id, 'category', 'ka', e.target.value)} placeholder="მაგ. მუზეუმი, არტ ფეარი..." className={INPUT_CLS + " pl-10"} />
                          </div>
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                          <label className={LABEL_CLS}>Description</label>
                          <div className="relative">
                            <span className="absolute left-3.5 top-3 text-[8px] font-bold uppercase tracking-[0.15em] text-black/25">EN</span>
                            <textarea value={partner.description.en} onChange={(e) => updateBil(partner._id, 'description', 'en', e.target.value)} placeholder="Description..." className={INPUT_CLS + " pl-10 resize-none min-h-[60px]"} rows={2} />
                          </div>
                          <div className="relative">
                            <span className="absolute left-3.5 top-3 text-[8px] font-bold uppercase tracking-[0.15em] text-black/25">KA</span>
                            <textarea value={partner.description.ka} onChange={(e) => updateBil(partner._id, 'description', 'ka', e.target.value)} placeholder="აღწერა..." className={INPUT_CLS + " pl-10 resize-none min-h-[60px]"} rows={2} />
                          </div>
                        </div>

                        {/* URL */}
                        <div>
                          <label className={LABEL_CLS}>Website URL</label>
                          <input
                            value={partner.url}
                            onChange={(e) => updatePartner(partner._id, { url: e.target.value })}
                            placeholder="https://..."
                            className={INPUT_CLS}
                          />
                        </div>

                        {/* Logo */}
                        <div>
                          <label className={LABEL_CLS}>Logo</label>
                          <div className="flex items-center gap-3">
                            <div
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = 'image/*,.tif,.tiff';
                                input.onchange = (e) => {
                                  const f = (e.target as HTMLInputElement).files?.[0];
                                  if (f) handleLogoUpload(partner._id, f);
                                };
                                input.click();
                              }}
                              className="w-16 h-16 rounded-xl bg-black/[0.03] border border-dashed border-black/[0.1] flex items-center justify-center cursor-pointer hover:border-black/20 transition-all overflow-hidden shrink-0"
                            >
                              {logoLoading === partner._id ? (
                                <Loader2 size={16} className="text-black/25 animate-spin" />
                              ) : partner.logo ? (
                                <img src={partner.logo} alt="" className="w-full h-full object-contain p-1" />
                              ) : (
                                <Upload size={16} className="text-black/15" />
                              )}
                            </div>
                            {partner.logo && (
                              <button
                                onClick={() => updatePartner(partner._id, { logo: '' })}
                                className="text-[9px] font-bold uppercase tracking-wider text-red-500 hover:bg-red-500/5 px-2 py-1 rounded-lg transition-all"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </Reorder.Item>
              );
            })}
          </AnimatePresence>
        </Reorder.Group>
      )}

      <AdminConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Partner"
        message="This will permanently remove this partner. This action cannot be undone."
      />
    </div>
  );
};

export default AdminPartnersPage;
